import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';
import { roundIfNumeric } from '@/features/battery/utils/NumberUtils';
import { handleVinSearchToken } from '@/features/battery/workshop/BatteryOverview';
import { THRESHOLD_DANGER, THRESHOLD_WARNING } from '@/features/batteryHistoryGraph/BatteryHistoryGraph';
import {
    BatteryHealthMonitoring,
    BatteryPacksResponse,
    ExternalActivationState,
    ExternalHistoricalBatteryHealthResponse,
} from '@/generated/GeneratedApiTypes';

const roundFields = (data?: BatteryHealthMonitoring) => {
    if (!data) {
        return {};
    }
    const newData = { ...data };
    if (data.health_data !== undefined && newData.health_data !== undefined) {
        newData.health_data.system.full_charge_energy = roundIfNumeric(data.health_data.system.full_charge_energy);
    }
    return newData;
};

export const formatGraphData = (
    data: ExternalHistoricalBatteryHealthResponse,
): ExternalHistoricalBatteryHealthResponse => {
    const newData = { ...data };

    if (newData.battery_health_records === undefined) {
        return {
            battery_type: newData.battery_type,
            historical_data_status: newData.historical_data_status,
            battery_health_records: [],
        };
    }

    return {
        battery_type: newData.battery_type,
        historical_data_status: newData.historical_data_status,
        battery_health_records: newData.battery_health_records.map((historicalData) => ({
            ...historicalData,
            state_of_health_status: historicalData.state_of_health_status?.toLowerCase(),
            threshold_warning: THRESHOLD_WARNING,
            threshold_danger: THRESHOLD_DANGER,
        })),
    };
};

export const batteryApi = createApi({
    reducerPath: 'batteryApi',
    baseQuery: fetchBaseQuery({ baseUrl: `${import.meta.env.VITE_BACKEND_URI}`, prepareHeaders }),
    endpoints: (builder) => ({
        fetchBatteryLevels: builder.query<BatteryHealthMonitoring, string>({
            query: (assetId) => ({
                url: `/battery-overview/${assetId}`,
                headers: { 'X-auth': handleVinSearchToken(assetId) },
            }),
            transformResponse: roundFields,
        }),
        fetchHistoricalBatteryHealth: builder.query<ExternalHistoricalBatteryHealthResponse, string>({
            query: (assetId) => ({
                url: `/battery-historical-health/${assetId}`,
                headers: { 'X-auth': handleVinSearchToken(assetId) },
            }),
            transformResponse: formatGraphData,
        }),
        fetchProductForAsset: builder.query<ExternalActivationState, string>({
            query: (assetId) => ({
                url: `activation-state/${assetId}`,
                headers: { 'X-auth': handleVinSearchToken(assetId) },
            }),
        }),
        fetchBatteryPackLevels: builder.query<BatteryPacksResponse, string>({
            query: (assetId) => ({
                url: `/battery-overview/${assetId}/packs`,
                headers: { 'X-auth': handleVinSearchToken(assetId) },
            }),
        }),
    }),
});

export const {
    useFetchBatteryLevelsQuery,
    useFetchHistoricalBatteryHealthQuery,
    useFetchProductForAssetQuery,
    useFetchBatteryPackLevelsQuery,
} = batteryApi;
